import { useState, type ReactNode } from "react";

export interface ImageWithFallbackProps {
  src?: string | null;
  alt: string;
  className?: string;
  /** Rendered when there is no `src`, or when the browser cannot load it. */
  fallback: ReactNode;
}

/**
 * An `<img>` that degrades to a placeholder instead of a broken-image icon.
 *
 * Guarding on `src` alone is not enough: a stored URL can point at an object
 * that has been deleted, moved, or was mistyped into a free-text field, and the
 * browser renders its own broken glyph in that case (T-202). The one request
 * needed to discover that is unavoidable — but it happens once per src, and a
 * null src still makes no request at all.
 */
export function ImageWithFallback({ src, alt, className, fallback }: ImageWithFallbackProps) {
  const [failedSrc, setFailedSrc] = useState<string | null>(null);

  if (!src || failedSrc === src) return <>{fallback}</>;

  return (
    <img
      src={src}
      alt={alt}
      className={className}
      onError={() => setFailedSrc(src)}
    />
  );
}
